import { useMemo } from 'react';
import { ShieldAlert, Wrench } from 'lucide-react';
import type { Asset, MaintenanceItem } from '@app/shared';
import type { AttentionItem, SearchResult, WebModule } from '../../app/types.ts';
import { familyRecords, scoreText } from '../../platform/search.ts';
import { daysUntil, dueLabelInline } from '../../platform/time.ts';
import { dueInfo, KIND_LABELS, useAssets, useMaintenanceItems } from './shared.ts';

function useMaintenanceAttention(): AttentionItem[] {
  const assets = useAssets();
  const items = useMaintenanceItems();
  return useMemo(() => {
    const byId = new Map((assets ?? []).filter((a) => !a.archived).map((a) => [a.id, a]));
    const out: AttentionItem[] = [];
    for (const i of items ?? []) {
      const asset = byId.get(i.assetId);
      if (!asset || i.kind !== 'schedule') continue;
      const d = dueInfo(i, asset);
      if (!d.urgent) continue;
      out.push({
        key: `maint:${i.id}`,
        title: `${asset.name}: ${i.title}`,
        detail: d.dueOn ? `Due ${dueLabelInline(d.dueOn)}` : `${d.milesLeft} ${asset.mileageUnit} to go`,
        to: `/home-care/${asset.id}`,
        tone: d.overdue ? 'danger' : 'warning',
        icon: Wrench,
        days: d.days ?? (d.overdue ? -1 : 7),
      });
    }
    for (const a of byId.values()) {
      if (!a.warrantyExpiresOn) continue;
      const days = daysUntil(a.warrantyExpiresOn);
      if (days > 30 || days < -7) continue;
      out.push({
        key: `warranty:${a.id}`,
        title: `${a.name} warranty`,
        detail: `${days < 0 ? 'Ended' : 'Ends'} ${dueLabelInline(a.warrantyExpiresOn)}`,
        to: `/home-care/${a.id}`,
        tone: 'warning',
        icon: ShieldAlert,
        days,
      });
    }
    return out;
  }, [assets, items]);
}

export const maintenanceModule: WebModule = {
  id: 'maintenance',
  label: 'Home & car',
  icon: Wrench,
  path: '/home-care',
  nav: { group: 'household', order: 30 },
  routes: [
    { path: 'home-care', lazy: async () => ({ Component: (await import('./AssetsScreen.tsx')).AssetsScreen }) },
    { path: 'home-care/:assetId', lazy: async () => ({ Component: (await import('./AssetScreen.tsx')).AssetScreen }) },
  ],
  useAttention: useMaintenanceAttention,
  async search(q, ctx) {
    const out: SearchResult[] = [];
    const assets = await familyRecords<Asset>('assets', ctx.familyId);
    const names = new Map(assets.map((a) => [a.id, a.name]));
    for (const a of assets) {
      const score = scoreText(q, [a.name, a.make, a.model, a.location, a.licensePlate, a.serialNumber, a.notes]);
      if (score)
        out.push({ key: `assets:${a.id}`, title: a.name, subtitle: KIND_LABELS[a.kind], to: `/home-care/${a.id}`, group: 'Home & car', icon: Wrench, score });
    }
    for (const i of await familyRecords<MaintenanceItem>('maintenance_items', ctx.familyId)) {
      const score = scoreText(q, [i.title, i.provider, i.notes]);
      if (score && names.has(i.assetId))
        out.push({
          key: `maintenance_items:${i.id}`,
          title: i.title,
          subtitle: names.get(i.assetId),
          to: `/home-care/${i.assetId}`,
          group: 'Home & car',
          icon: Wrench,
          score,
        });
    }
    return out;
  },
};
